import type { MaybeRefOrGetter } from 'vue';
import type { Filter, GlobalCollectionId } from '#layers/data-model';
import type { ListingView, ListingViewUserOverride } from '~/types/listing';
import { useLocalStorage } from '@vueuse/core';
import { computed, nextTick, ref, toValue, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { callOrReturn, deepToRaw, isDeepEqual } from '#layers/base';
import { isNullishOrEmpty } from '#layers/base/utils/iterable';
import { isObjectEmpty, omit } from '#layers/base/utils/object';
import { useBlockEditorLibrary } from '#layers/block-engine';
import { useBlockStorageKey } from '~/composables/use-block-storage-key';
import { usePersistedBlockState } from '~/composables/use-persisted-block-state';
import { PROJECT_COLLECTIONS } from '~/constants/collections';
import { useProjectSchemaStore } from '~/stores/project-schema';

export const DEFAULT_VIEW_ID = '$default' as const;

export interface UseListingViewsOptions {
	collectionId: MaybeRefOrGetter<GlobalCollectionId | null>;
	keyPrefix?: string;
	defaultViewOverride?: MaybeRefOrGetter<Partial<ListingView> | undefined>;
}

function withoutLegacySearchTerm<T extends { searchTerm?: string }>(value: T): Omit<T, 'searchTerm'> {
	return omit(value, ['searchTerm']);
}

export function useListingViews(options: UseListingViewsOptions) {
	const blockEditorLibrary = useBlockEditorLibrary();
	const { t } = useI18n();
	const { findCollectionById } = useProjectSchemaStore();
	const collectionId = computed(() => toValue(options.collectionId));
	const shouldPersistViews = computed(() => collectionId.value !== null);
	const keyPrefix = options.keyPrefix;

	function prefixedKey(base: string) {
		return keyPrefix ? `${keyPrefix}:${base}` : base;
	}

	const { storageKey: lastViewStorageKey } = useBlockStorageKey(prefixedKey('listing-last-view'), {
		collectionId: options.collectionId,
		scope: 'user',
	});

	function getLayoutName(layoutId: string): string {
		const block = blockEditorLibrary.getBlock(layoutId);
		return block?.name || layoutId;
	}

	function getViewName(view: ListingView): string | undefined {
		if (!isNullishOrEmpty(view.name)) {
			return view.name;
		}
		else {
			if (view.id === DEFAULT_VIEW_ID) {
				return t('listing.views.all_items');
			}

			return getLayoutName(view.layoutId);
		}
	}

	function getDefaultView(defaults?: Partial<ListingView>, overrides?: Partial<ListingView>): ListingView {
		let collectionDefaultView: Partial<ListingView> = {};

		if (collectionId.value) {
			const collection = findCollectionById(collectionId.value);

			if (collection?.apiName) {
				const collectionConfig = PROJECT_COLLECTIONS[collection.apiName as keyof typeof PROJECT_COLLECTIONS];

				if (collectionConfig?.listing?.defaultView) {
					const defaultViewConfig = collectionConfig.listing.defaultView;
					collectionDefaultView = callOrReturn(defaultViewConfig, collection);
				}
			}
		}

		const viewOverride = toValue(options.defaultViewOverride);

		return {
			...defaults,
			id: crypto.randomUUID(),
			layoutId: '$builtin:LayoutTable',
			filter: { _and: [] },
			searchTerm: '',
			...collectionDefaultView,
			...viewOverride,
			...overrides,
		};
	}

	const persistedDefaultView = usePersistedBlockState<Partial<ListingView>>(prefixedKey('listing-default-view'), {}, {
		collectionId: options.collectionId,
		scope: 'user',
		enabled: shouldPersistViews,
	});

	const persistedSharedDefaultView = usePersistedBlockState<Partial<ListingView>>(prefixedKey('listing-shared-default-view'), {}, {
		collectionId: options.collectionId,
		scope: 'global',
		enabled: shouldPersistViews,
	});

	const sharedDefaultView = computed<ListingView>(() => {
		return getDefaultView(
			{},
			{
				id: DEFAULT_VIEW_ID,
				...persistedSharedDefaultView.value,
			},
		);
	});

	const views = usePersistedBlockState<ListingView[]>(prefixedKey('listing-views'), [], {
		collectionId: options.collectionId,
		scope: 'global',
		enabled: shouldPersistViews,
	});

	const viewOverrides = usePersistedBlockState<Record<string, ListingViewUserOverride>>(
		prefixedKey('listing-view-overrides'),
		{},
		{
			collectionId: options.collectionId,
			scope: 'user',
			enabled: shouldPersistViews,
		},
	);

	const currentViewId = useLocalStorage<string>(lastViewStorageKey, DEFAULT_VIEW_ID);

	// Shared views are global; each user layers a personal override on top holding filter,
	// layoutId and displayOptions. These auto-persist to the override on every change (never
	// silently to the shared view), so one user's tweaks stay private. Search is session-only.
	// saveForEveryone() promotes filter/layout/display to the shared view and resetToShared()
	// discards them; both then clear the override. Only name/icon write straight to a named shared view.
	function removeOverride(viewId: string) {
		if (!(viewId in viewOverrides.value)) return;
		viewOverrides.value = omit(viewOverrides.value, [viewId]);
	}

	function removeOverrideFields(viewId: string, fields: (keyof ListingViewUserOverride)[]) {
		const existing = viewOverrides.value[viewId];
		if (!existing) return;

		const fieldsToRemove: (keyof ListingViewUserOverride)[] = [...fields, 'searchTerm'];
		if (!fieldsToRemove.some((field) => field in existing)) return;

		const rest = omit(existing, fieldsToRemove);

		if (isObjectEmpty(rest)) {
			removeOverride(viewId);
		}
		else {
			viewOverrides.value = { ...viewOverrides.value, [viewId]: rest };
		}
	}

	function setOverride(viewId: string, patch: Partial<ListingViewUserOverride>) {
		const existingOverride = withoutLegacySearchTerm(viewOverrides.value[viewId] ?? {});
		const nextOverride = { ...existingOverride, ...patch };
		if (isDeepEqual(nextOverride, existingOverride)) return;

		viewOverrides.value = {
			...viewOverrides.value,
			[viewId]: nextOverride,
		};
	}

	function cloneDisplayOptions(displayOptions: ListingView['displayOptions']): ListingView['displayOptions'] {
		return displayOptions ? structuredClone(deepToRaw(displayOptions)) : undefined;
	}

	function sharedViewFor(viewId: string): ListingView | undefined {
		return viewId === DEFAULT_VIEW_ID
			? sharedDefaultView.value
			: views.value.find((v) => v.id === viewId);
	}

	function personalViewOverrideFor(viewId: string): Partial<ListingView> {
		const override = withoutLegacySearchTerm(viewOverrides.value[viewId] ?? {});
		if (viewId !== DEFAULT_VIEW_ID) return override;

		return {
			...withoutLegacySearchTerm(persistedDefaultView.value),
			...override,
		};
	}

	function withDisplayOverride(view: ListingView): ListingView {
		const override = personalViewOverrideFor(view.id);
		return {
			...view,
			name: override.name ?? view.name,
			icon: override.icon ?? view.icon,
			layoutId: override?.layoutId ?? view.layoutId,
			displayOptions: cloneDisplayOptions(override?.displayOptions ?? view.displayOptions),
		};
	}

	const defaultView = computed<ListingView>(() => withDisplayOverride(sharedDefaultView.value));
	const effectiveViews = computed<ListingView[]>(() => views.value.map((view) => withDisplayOverride(view)));

	const currentView = computed<ListingView>({
		get() {
			const sharedView = sharedViewFor(currentViewId.value);

			if (!sharedView) {
				return defaultView.value;
			}

			return withDisplayOverride(sharedView);
		},
		set(value) {
			if (currentViewId.value === DEFAULT_VIEW_ID) {
				const sharedView = sharedDefaultView.value;
				const nextOverride = {
					layoutId: value.layoutId,
					name: value.name,
					icon: value.icon,
					displayOptions: cloneDisplayOptions(value.displayOptions),
				};

				if (
					nextOverride.layoutId === sharedView.layoutId
					&& nextOverride.name === sharedView.name
					&& nextOverride.icon === sharedView.icon
					&& isDeepEqual(nextOverride.displayOptions, sharedView.displayOptions)
				) {
					persistedDefaultView.value = {};
				}
				else {
					persistedDefaultView.value = nextOverride;
				}

				return;
			}

			const index = views.value.findIndex((v) => v.id === currentViewId.value);
			if (index === -1) return;

			const sharedView = views.value[index]!;

			// Identity (name/icon) is shared — renaming applies for everyone.
			if (value.name !== sharedView.name || value.icon !== sharedView.icon) {
				views.value[index] = { ...withoutLegacySearchTerm(sharedView), name: value.name, icon: value.icon };
			}

			// Layout and display options are personal: keep them in the override.
			const effective = currentView.value;

			if (value.layoutId !== effective.layoutId || !isDeepEqual(value.displayOptions, effective.displayOptions)) {
				if (value.layoutId === sharedView.layoutId && isDeepEqual(value.displayOptions, sharedView.displayOptions)) {
					removeOverrideFields(currentViewId.value, ['layoutId', 'displayOptions']);
				}
				else {
					setOverride(currentViewId.value, {
						layoutId: value.layoutId,
						displayOptions: cloneDisplayOptions(value.displayOptions),
					});
				}
			}
		},
	});

	const currentFilter = ref<Filter>({ _and: [] });
	const currentSearchTerm = ref<string>('');

	const currentSharedDisplayOptions = computed(() => {
		return sharedViewFor(currentViewId.value)?.displayOptions;
	});

	let skipAutoSave = false;

	watch(currentViewId, () => {
		skipAutoSave = true;
		// Ignore a stale override left behind for a view another user deleted.
		const sharedView = sharedViewFor(currentViewId.value);
		const override = sharedView ? viewOverrides.value[currentViewId.value] : undefined;
		const baseline = sharedView ?? defaultView.value;
		currentFilter.value = structuredClone(deepToRaw(override?.filter ?? baseline.filter ?? { _and: [] }));
		currentSearchTerm.value = '';

		nextTick(() => {
			skipAutoSave = false;
		});
	}, { immediate: true });

	watch(currentFilter, (filter) => {
		if (skipAutoSave) return;

		const baseline = sharedViewFor(currentViewId.value);
		if (!baseline) return;

		const hasFilterChange = !isDeepEqual(filter, baseline.filter ?? { _and: [] });

		if (hasFilterChange) {
			setOverride(currentViewId.value, {
				filter: structuredClone(deepToRaw(filter)),
			});

			return;
		}

		// Filter matches the shared view again: drop that override field,
		// but keep any personal layout/display override for this view.
		removeOverrideFields(currentViewId.value, ['filter']);
	}, { deep: true });

	const differsFromSharedDisplay = computed(() => {
		const shared = sharedViewFor(currentViewId.value);
		if (!shared) return false;

		const current = currentView.value;
		return current.layoutId !== shared.layoutId || !isDeepEqual(current.displayOptions, shared.displayOptions);
	});

	const differsFromSharedView = computed(() => {
		const shared = sharedViewFor(currentViewId.value);
		if (!shared) return false;

		return (
			!isDeepEqual(currentFilter.value, shared.filter ?? { _and: [] })
			|| differsFromSharedDisplay.value
		);
	});

	function saveForEveryone() {
		if (currentViewId.value === DEFAULT_VIEW_ID) {
			const effective = currentView.value;

			persistedSharedDefaultView.value = {
				...withoutLegacySearchTerm(sharedDefaultView.value),
				layoutId: effective.layoutId,
				name: effective.name,
				icon: effective.icon,
				displayOptions: cloneDisplayOptions(effective.displayOptions),
				filter: structuredClone(deepToRaw(currentFilter.value)),
			};

			persistedDefaultView.value = {};
			removeOverride(DEFAULT_VIEW_ID);
			return;
		}

		const index = views.value.findIndex((v) => v.id === currentViewId.value);
		if (index === -1) return;

		const existingView = views.value[index]!;
		const effective = currentView.value;

		views.value[index] = {
			...withoutLegacySearchTerm(existingView),
			layoutId: effective.layoutId,
			displayOptions: cloneDisplayOptions(effective.displayOptions),
			filter: structuredClone(deepToRaw(currentFilter.value)),
		};

		removeOverride(currentViewId.value);
	}

	function resetToShared() {
		removeOverride(currentViewId.value);

		if (currentViewId.value === DEFAULT_VIEW_ID) {
			persistedDefaultView.value = {};
		}

		// currentView now resolves without the override (default view falls back to defaultView).
		const sharedView = currentView.value;
		currentFilter.value = structuredClone(deepToRaw(sharedView.filter ?? { _and: [] }));
		currentSearchTerm.value = '';
	}

	function createNewView(options?: { id?: string; baseName?: string; fromCurrentState?: boolean; asCopy?: boolean }): ListingView {
		const fromCurrentState = options?.fromCurrentState ?? true;

		let baseName = options?.baseName;

		if (options?.asCopy && !baseName) {
			const currentViewName = getViewName(currentView.value);
			baseName = currentViewName ? t('listing.views.copy_name', { name: currentViewName }) : undefined;
		}

		const newView: ListingView = {
			id: options?.id ?? crypto.randomUUID(),
			name: baseName,
			layoutId: currentView.value.layoutId,
			displayOptions: structuredClone(deepToRaw(currentView.value.displayOptions ?? {})),
			filter: fromCurrentState ? structuredClone(deepToRaw(currentFilter.value)) : structuredClone(deepToRaw(currentView.value.filter ?? {})),
			icon: currentView.value.icon,
		};

		views.value.push(newView);
		currentViewId.value = newView.id;
		return newView;
	}

	function duplicateView(options?: { baseName?: string; asCopy?: boolean; fromCurrentState?: boolean }) {
		const fromCurrentState = options?.fromCurrentState ?? false;
		return createNewView({ ...options, fromCurrentState, asCopy: true });
	}

	function addView(newViewId: ListingView['id']) {
		createNewView({ id: newViewId, fromCurrentState: true });
	}

	function deleteCurrentView() {
		const viewIndex = views.value.findIndex((view) => view.id === currentViewId.value);
		if (viewIndex === -1) return;

		removeOverride(currentViewId.value);
		views.value = views.value.filter((view) => view.id !== currentViewId.value);
		currentViewId.value = views.value[Math.max(0, viewIndex - 1)]?.id ?? DEFAULT_VIEW_ID;
	}

	return {
		views,
		effectiveViews,
		currentViewId,
		currentView,
		currentSharedDisplayOptions,
		defaultView,
		currentFilter,
		currentSearchTerm,
		differsFromSharedView,
		differsFromSharedDisplay,
		addView,
		duplicateView,
		saveForEveryone,
		resetToShared,
		deleteCurrentView,
		getLayoutName,
		getViewName,
	};
}
